import { useState, useEffect } from 'react';

import { Switch } from '@@/form-components/SwitchField/Switch';
import { Input } from '@@/form-components/Input';
import { Select } from '@@/form-components/ReactSelect';

import { AlertRuleTier } from '../../types';
import { AlertSeverityBadge } from '../../AlertSeverityBadge';
import {
  ByteUnit,
  byteUnitOptions,
  convertBytesToHumanReadable,
  formatDisplayValue,
} from '../../bytes';
import { canonicalizeAlertRuleTiers } from '../../utils';

import { computeTierErrors } from './tierValidation';

interface TierRowProps {
  tier: AlertRuleTier;
  metricType: 'percentage' | 'bytes' | 'raw' | 'days';
  hintId: string;
  error?: string;
  onChange: (updated: AlertRuleTier) => void;
}

function TierRow({ tier, metricType, hintId, error, onChange }: TierRowProps) {
  const [displayValue, setDisplayValue] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<ByteUnit>('MB');

  const errorId = error ? `tier-threshold-error-${tier.severity}` : undefined;
  const describedBy = errorId ? `${hintId} ${errorId}` : hintId;

  useEffect(() => {
    if (metricType === 'bytes') {
      if (tier.threshold === 0) {
        setDisplayValue('0');
        return;
      }
      const { value, unit } = convertBytesToHumanReadable(tier.threshold);
      setDisplayValue(value.toString());
      setSelectedUnit(unit);
    } else {
      setDisplayValue(tier.threshold.toString());
    }
  }, [tier.threshold, metricType]);

  function handleDisplayValueChange(raw: string) {
    setDisplayValue(raw);
    const numeric = parseFloat(raw);
    if (Number.isNaN(numeric)) {
      onChange({ ...tier, threshold: 0 });
      return;
    }
    if (metricType === 'bytes') {
      const unitConfig = byteUnitOptions.find((u) => u.value === selectedUnit);
      onChange({
        ...tier,
        threshold: Math.round(numeric * (unitConfig?.multiplier ?? 1)),
      });
    } else {
      onChange({ ...tier, threshold: numeric });
    }
  }

  function handleUnitChange(unit: ByteUnit) {
    const numeric = parseFloat(displayValue);
    if (Number.isNaN(numeric)) {
      setSelectedUnit(unit);
      return;
    }

    const previousUnitConfig = byteUnitOptions.find(
      (u) => u.value === selectedUnit
    );
    const nextUnitConfig = byteUnitOptions.find((u) => u.value === unit);

    if (!previousUnitConfig || !nextUnitConfig) {
      setSelectedUnit(unit);
      return;
    }

    const bytesValue = Math.round(numeric * previousUnitConfig.multiplier);
    const convertedDisplayValue = bytesValue / nextUnitConfig.multiplier;

    setSelectedUnit(unit);
    setDisplayValue(formatDisplayValue(convertedDisplayValue));

    onChange({
      ...tier,
      threshold: bytesValue,
    });
  }

  return (
    <tr>
      <td className="w-28 py-3 pl-4 pr-2 align-middle">
        <Switch
          id={`tier-enabled-${tier.severity}`}
          name={`tier-enabled-${tier.severity}`}
          checked={tier.enabled}
          onChange={(checked) => onChange({ ...tier, enabled: checked })}
          data-cy={`tier-enabled-${tier.severity}`}
          showStateIcon
        />
      </td>
      <td className="w-40 px-2 py-3 align-middle">
        <AlertSeverityBadge severity={tier.severity} />
      </td>
      <td className="py-3 pl-2 pr-4 align-middle">
        <div className="flex items-center gap-3">
          <div className={metricType === 'bytes' ? 'w-36' : 'w-48'}>
            <Input
              id={`tier-threshold-${tier.severity}`}
              name={`tier-threshold-${tier.severity}`}
              type="number"
              value={displayValue}
              onChange={(e) => handleDisplayValueChange(e.target.value)}
              min={metricType === 'percentage' ? 0 : undefined}
              max={metricType === 'percentage' ? 100 : undefined}
              step={metricType === 'bytes' ? 'any' : 1}
              disabled={!tier.enabled}
              aria-describedby={describedBy}
              data-cy={`tier-threshold-${tier.severity}`}
            />
          </div>
          {metricType === 'bytes' && (
            <div className="w-20">
              <Select
                id={`tier-unit-${tier.severity}`}
                options={byteUnitOptions}
                value={byteUnitOptions.find((u) => u.value === selectedUnit)}
                onChange={(opt) =>
                  opt && handleUnitChange(opt.value as ByteUnit)
                }
                isSearchable={false}
                isDisabled={!tier.enabled}
                data-cy={`tier-unit-${tier.severity}`}
              />
            </div>
          )}
          {metricType === 'percentage' && (
            <span className="text-muted text-lg leading-none">%</span>
          )}
          {metricType === 'days' && (
            <span className="text-muted text-sm leading-none">days</span>
          )}
        </div>
        {error && (
          <p id={errorId} className="text-danger mt-1 text-xs" role="alert">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}

interface TierConfigPanelProps {
  tiers: AlertRuleTier[];
  operator: string;
  metricType: 'percentage' | 'bytes' | 'raw' | 'days';
  onChange: (tiers: AlertRuleTier[]) => void;
}

export function TierConfigPanel({
  tiers,
  operator,
  metricType,
  onChange,
}: TierConfigPanelProps) {
  const hintId = 'tier-threshold-ordering-hint';
  const errors = computeTierErrors(tiers, operator);
  const ordered = canonicalizeAlertRuleTiers(tiers);

  function handleTierChange(updated: AlertRuleTier) {
    const next = ordered.map((t) =>
      t.severity === updated.severity ? updated : t
    );

    onChange(next);
  }

  return (
    <div data-cy="tier-config-panel">
      <table className="mb-0 table w-full table-fixed">
        <thead>
          <tr>
            <th className="w-28">Active</th>
            <th className="w-40">Severity</th>
            <th>Threshold</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((tier) => (
            <TierRow
              key={tier.severity}
              tier={tier}
              metricType={metricType}
              hintId={hintId}
              error={errors[tier.severity]}
              onChange={handleTierChange}
            />
          ))}
        </tbody>
      </table>
      <p id={hintId} className="text-muted mt-3 text-sm">
        {operator === '>'
          ? 'Thresholds must increase from info → warning → critical.'
          : 'Thresholds must decrease from info → warning → critical.'}
      </p>
    </div>
  );
}
